import { Guid } from "@common/guid";
import { ProcessFieldOrderItem } from "../Process.types";

// Mirror of Backend/Core/Filtering/Processes/ProcessColumn.cs. Values are explicit and must stay in step: they travel
// in the search request, in sortByColumn and in the saved-view blobs the backend serves.
export enum ProcessColumn {
    UserFriendlyId = 0,
    Name = 1,
    Status = 2,
    Description = 3,
    Note = 4,
    BusinessAreas = 5,
    ComplianceAreas = 6,
    Tags = 7,
    Responsible = 8,
    AdditionalUsers = 9,
    AccessBusinessAreas = 10,
    ExcludedUsers = 11,
    VisibleToAllUsers = 12,
    GroupEntities = 13,
    Assets = 14,
    LegalEntities = 15,
    Template = 16,
    CreatedAt = 17,
    ProcessingActivities = 18,
    RiskAreas = 19,
}

// The string ids the column picker, the table headers and user-saved views work in.
export const ProcessColumnId = {
    userFriendlyId: "userFriendlyId",
    name: "name",
    status: "status",
    description: "description",
    note: "note",
    businessAreas: "businessAreas",
    complianceAreas: "complianceAreas",
    tags: "tags",
    responsible: "responsible",
    additionalUsers: "additionalUsers",
    accessBusinessAreas: "accessBusinessAreas",
    excludedUsers: "excludedUsers",
    visibleToAllUsers: "visibleToAllUsers",
    groupEntities: "groupEntities",
    assets: "assets",
    legalEntities: "legalEntities",
    template: "template",
    createdAt: "createdAt",
    processingActivities: "processingActivities",
    riskAreas: "riskAreas",
} as const;

export const processColumnByFilterId: Record<string, ProcessColumn> = {
    [ProcessColumnId.userFriendlyId]: ProcessColumn.UserFriendlyId,
    [ProcessColumnId.name]: ProcessColumn.Name,
    [ProcessColumnId.status]: ProcessColumn.Status,
    [ProcessColumnId.description]: ProcessColumn.Description,
    [ProcessColumnId.note]: ProcessColumn.Note,
    [ProcessColumnId.businessAreas]: ProcessColumn.BusinessAreas,
    [ProcessColumnId.complianceAreas]: ProcessColumn.ComplianceAreas,
    [ProcessColumnId.tags]: ProcessColumn.Tags,
    [ProcessColumnId.responsible]: ProcessColumn.Responsible,
    [ProcessColumnId.additionalUsers]: ProcessColumn.AdditionalUsers,
    [ProcessColumnId.accessBusinessAreas]: ProcessColumn.AccessBusinessAreas,
    [ProcessColumnId.excludedUsers]: ProcessColumn.ExcludedUsers,
    [ProcessColumnId.visibleToAllUsers]: ProcessColumn.VisibleToAllUsers,
    [ProcessColumnId.groupEntities]: ProcessColumn.GroupEntities,
    [ProcessColumnId.assets]: ProcessColumn.Assets,
    [ProcessColumnId.legalEntities]: ProcessColumn.LegalEntities,
    [ProcessColumnId.template]: ProcessColumn.Template,
    [ProcessColumnId.createdAt]: ProcessColumn.CreatedAt,
    [ProcessColumnId.processingActivities]: ProcessColumn.ProcessingActivities,
    [ProcessColumnId.riskAreas]: ProcessColumn.RiskAreas,
};

// Derived from the map above rather than written out a second time, so the two cannot fall out of step.
const processFilterIdByColumn: Record<number, string> = Object.fromEntries(
    Object.entries(processColumnByFilterId).map(([filterId, column]) => [column, filterId])
);

export function getProcessFilterIdByColumn(column: ProcessColumn): string | undefined {
    return processFilterIdByColumn[column];
}

// A display field id is either one of the names above or the bare guid of a template custom field.
export function toProcessDisplayFields(filterIds: Array<string>): Array<ProcessFieldOrderItem> {
    return filterIds
        .map((filterId, index) =>
            Guid.isGuid(filterId) ? { customFieldId: filterId, displayOrder: index } : { column: processColumnByFilterId[filterId], displayOrder: index }
        )
        .filter((field) => field.customFieldId !== undefined || field.column !== undefined);
}
